import { useEffect, useRef, useState } from 'react';
import { User as UserIcon, ImagePlus, Loader2, KeyRound } from 'lucide-react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import { compressAvatarToDataUrl } from '../blog/editor/imageUpload';
import { syncAuthorAcrossPosts } from '../blog/authorSync';
import { useDashboardProfile } from '../context/DashboardProfileContext';

/**
 * "Minha conta" — nome de exibição + foto (câmera ou galeria: o próprio
 * <input type=file accept="image/*"> sem `capture` já abre o seletor nativo
 * com as duas opções no mobile) e troca de senha.
 *
 * Nome/foto vão pra dashboardUsers/{uid} no Firestore, não pro campo nativo
 * auth.currentUser.photoURL — que tem limite de ~2KB, curto demais pra uma
 * imagem (mesma razão do authors/{uid} do blog, que fica noutro projeto
 * Firebase). O nome também atualiza displayName no Auth, já que é o único
 * lugar do código que ainda lê isso direto (fallback de autoria de post novo
 * no blog — ver BlogEditorPage.tsx).
 */
export function AccountDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { user, getIdToken } = useAuth();
  const { profile, refresh } = useDashboardProfile();
  const fileRef = useRef<HTMLInputElement>(null);

  const [nome, setNome] = useState('');
  const [fotoUrl, setFotoUrl] = useState('');
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaved, setPasswordSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNome(profile?.nome || user?.displayName || '');
    setFotoUrl(profile?.fotoUrl ?? '');
    setProfileError(null);
    setProfileSaved(false);
    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarSenha('');
    setPasswordError(null);
    setPasswordSaved(false);
  }, [open, profile, user]);

  const handlePickPhoto = () => fileRef.current?.click();

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProcessingPhoto(true);
    setProfileError(null);
    try {
      const dataUrl = await compressAvatarToDataUrl(file);
      setFotoUrl(dataUrl);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Falha ao processar foto');
    } finally {
      setProcessingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user || !db) return;
    setSavingProfile(true);
    setProfileError(null);
    setProfileSaved(false);
    try {
      await setDoc(doc(db, 'dashboardUsers', user.uid), { nome, fotoUrl }, { merge: true });
      if (nome && nome !== user.displayName) {
        await updateProfile(user, { displayName: nome });
      }
      await refresh();
      setProfileSaved(true);

      // Não aguardado de propósito: atualiza a assinatura dos posts já
      // publicados por essa pessoa aos poucos, em segundo plano, sem travar
      // o diálogo (ver authorSync.ts pro porquê de ser gradual).
      const token = await getIdToken();
      if (token) void syncAuthorAcrossPosts(user.uid, token);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Falha ao salvar perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user || !user.email) return;
    setPasswordError(null);
    setPasswordSaved(false);
    if (novaSenha.length < 6) {
      setPasswordError('A nova senha precisa ter pelo menos 6 caracteres.');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setPasswordError('As senhas não coincidem.');
      return;
    }
    setSavingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, senhaAtual);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, novaSenha);
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');
      setPasswordSaved(true);
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setPasswordError('Senha atual incorreta.');
      } else if (code === 'auth/weak-password') {
        setPasswordError('Senha muito fraca — use pelo menos 6 caracteres.');
      } else if (code === 'auth/requires-recent-login') {
        setPasswordError('Por segurança, saia e entre de novo antes de trocar a senha.');
      } else {
        setPasswordError(err instanceof Error ? err.message : 'Falha ao trocar senha');
      }
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Minha conta</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            {profileError && <p className="text-sm text-destructive">{profileError}</p>}

            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handlePickPhoto}
                disabled={processingPhoto}
                title="Trocar foto"
                className="relative h-16 w-16 shrink-0 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                {processingPhoto ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : fotoUrl ? (
                  <img src={fotoUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <UserIcon className="h-6 w-6 text-muted-foreground" />
                )}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handlePhotoChange}
              />
              <Button type="button" variant="outline" size="sm" onClick={handlePickPhoto} disabled={processingPhoto}>
                <ImagePlus className="h-3.5 w-3.5" />
                {fotoUrl ? 'Trocar foto' : 'Escolher foto'}
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="conta-nome">Nome de exibição</Label>
              <Input id="conta-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Seu nome" />
            </div>

            <div className="flex items-center justify-between gap-3">
              {profileSaved && <p className="text-xs text-emerald-600 dark:text-emerald-400">Perfil salvo.</p>}
              <Button type="button" size="sm" className="ml-auto" onClick={handleSaveProfile} disabled={savingProfile}>
                {savingProfile && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Salvar perfil
              </Button>
            </div>
          </div>

          <div className="border-t border-border pt-5 space-y-3">
            <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <KeyRound className="h-3.5 w-3.5" />
              Trocar senha
            </p>

            {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
            {passwordSaved && <p className="text-xs text-emerald-600 dark:text-emerald-400">Senha atualizada.</p>}

            <div className="space-y-2">
              <Label htmlFor="senha-atual">Senha atual</Label>
              <Input
                id="senha-atual"
                type="password"
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nova-senha">Nova senha</Label>
              <Input
                id="nova-senha"
                type="password"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmar-senha">Confirmar nova senha</Label>
              <Input
                id="confirmar-senha"
                type="password"
                value={confirmarSenha}
                onChange={(e) => setConfirmarSenha(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleChangePassword}
              disabled={savingPassword || !senhaAtual || !novaSenha || !confirmarSenha}
            >
              {savingPassword && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Alterar senha
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
